import React from "react";
import { Checkbox } from "antd";
import styles from "../styles.module.scss";
import { DateFilterType, DateRange } from "../types";
import CustomSelect from "../../CustomSelect";
import dayjs from "dayjs";
import { FilterPanel, SearchInput, Toolbar, filterStyles } from "../../Filters";
import DateRangeFilter from "../../DateRangeFilter";
import { ORDER_TYPE_OPTIONS } from "../../../utils/formatters";
import { ALL_MANAGERS } from "../../ManagerSelect/useOrderManagers";

interface FilterOption {
  value: string;
  label: string;
}

interface CampaignFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  periodFilter: DateFilterType | null;
  onPeriodChange: (value: DateFilterType | null) => void;
  dateRange: DateRange;
  onDateChange: (field: "start" | "end", value: string) => void;
  selectedCountry: string;
  selectedPlatform: string;
  selectedStatus: string;
  selectedType: string;
  selectedManager: string;
  onCountryChange: (value: string) => void;
  onPlatformChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onManagerChange: (value: string) => void;
  managerOptions: FilterOption[];
  countryOptions: FilterOption[];
  statusOptions: FilterOption[];
  showInternal: boolean;
  onShowInternalChange: (value: boolean) => void;
}

const PLATFORM_OPTIONS = [
  { value: "all", label: "All platforms" },
  { value: "APP_STORE", label: "iOS" },
  { value: "GOOGLE_STORE", label: "Android" },
];

const CampaignFilters: React.FC<CampaignFiltersProps> = ({
  searchValue,
  onSearchChange,
  periodFilter,
  onPeriodChange,
  dateRange,
  onDateChange,
  selectedCountry,
  selectedPlatform,
  selectedStatus,
  selectedType,
  selectedManager,
  onCountryChange,
  onPlatformChange,
  onStatusChange,
  onTypeChange,
  onManagerChange,
  managerOptions,
  countryOptions,
  statusOptions,
  showInternal,
  onShowInternalChange,
}) => {
  // Filters in use: a phone counts them on its Filters button.
  const activeFilters = [
    selectedCountry !== "all",
    selectedPlatform !== "all",
    selectedStatus !== "all",
    selectedType !== "all",
    selectedManager !== ALL_MANAGERS,
    showInternal,
  ].filter(Boolean).length;

  return (
    <>
      <Toolbar>
        <SearchInput
          placeholder="Search by Id or Email"
          value={searchValue}
          onChange={onSearchChange}
        />
        <DateRangeFilter
          period={periodFilter}
          onPeriodChange={onPeriodChange}
          range={
            dateRange.start && dateRange.end
              ? [dayjs(dateRange.start), dayjs(dateRange.end)]
              : null
          }
          onRangeChange={(range) => {
            onDateChange("start", range ? range[0].format("YYYY-MM-DD") : "");
            onDateChange("end", range ? range[1].format("YYYY-MM-DD") : "");
          }}
        />
      </Toolbar>
      <FilterPanel active={activeFilters}>
        <div className={styles.select}>
          <CustomSelect
            value={selectedCountry}
            onChange={onCountryChange}
            options={countryOptions}
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedPlatform}
            onChange={onPlatformChange}
            options={PLATFORM_OPTIONS}
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedStatus}
            onChange={onStatusChange}
            options={statusOptions}
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedType}
            onChange={onTypeChange}
            options={ORDER_TYPE_OPTIONS}
            aria-label="Order type"
          />
        </div>
        <div className={styles.select}>
          <CustomSelect
            value={selectedManager}
            onChange={onManagerChange}
            options={managerOptions}
            aria-label="Filter by manager"
          />
        </div>
        <Checkbox
          checked={showInternal}
          onChange={(event) => onShowInternalChange(event.target.checked)}
          className={filterStyles.checkbox}
        >
          Show internal users
        </Checkbox>
      </FilterPanel>
    </>
  );
};

export default CampaignFilters;
